import { NavLink } from "react-router-dom";
import { ReactNode, useState } from "react";
import { supabase } from "../lib/supabase";
import { usePlatformAdmin } from "../lib/usePlatformAdmin";
import CommandPalette from "./CommandPalette";

interface LayoutProps {
  children: ReactNode;
}

const NAV: Array<{ to: string; label: string; phase?: number }> = [
  { to: "/", label: "Overview" },
  { to: "/tokens", label: "Tokens" },
  { to: "/companies", label: "Companies" },
  { to: "/users", label: "Users" },
  { to: "/accountants", label: "Accountants" },
  { to: "/subscriptions", label: "Subscriptions" },
  { to: "/server", label: "Server" },
  { to: "/tax", label: "Tax" },
  { to: "/deploys", label: "Deploys" },
  { to: "/audit", label: "Audit" },
  { to: "/run", label: "Run", phase: 4 },
];

/**
 * The command palette opens on Cmd/Ctrl+K. A phone has no such key, so the
 * top-bar Search button sends the same keydown the palette already listens for.
 */
function openCommandPalette() {
  window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true }));
}

// md+ is the desktop reading: a fixed 14rem sidebar beside the page, exactly as
// before. Below md the sidebar collapses into a top bar whose Menu button
// reveals the same nav above the page.
export default function Layout({ children }: LayoutProps) {
  const { identity } = usePlatformAdmin();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex min-h-full flex-col md:grid md:grid-cols-[14rem_1fr]">
      <header className="flex items-center justify-between gap-2 border-b border-border bg-panel px-4 py-3 md:hidden">
        <div className="text-sm font-semibold tracking-wide">Countet Ops</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openCommandPalette}
            className="min-h-9 rounded border border-border px-3 py-1.5 text-xs text-muted hover:bg-bg"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="ops-nav"
            className="min-h-9 rounded border border-border px-3 py-1.5 text-xs hover:bg-bg"
          >
            {menuOpen ? "Close" : "Menu"}
          </button>
        </div>
      </header>
      <aside
        id="ops-nav"
        className={[
          menuOpen ? "flex" : "hidden",
          "flex-col border-b border-border bg-panel md:flex md:border-b-0 md:border-r",
        ].join(" ")}
      >
        <div className="hidden px-4 py-5 text-sm font-semibold tracking-wide md:block">
          Countet Ops
        </div>
        <nav className="flex-1 space-y-1 px-2 py-2 text-sm md:py-0">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                [
                  "flex items-center justify-between rounded px-3 py-2",
                  "hover:bg-bg",
                  isActive ? "bg-bg text-fg" : "text-muted",
                  item.phase ? "opacity-60" : "",
                ].join(" ")
              }
            >
              <span>{item.label}</span>
              {item.phase ? (
                <span className="rounded bg-border px-1.5 py-0.5 text-[10px]">
                  P{item.phase}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3 text-xs text-muted">
          <div className="truncate">{identity?.email ?? "—"}</div>
          <button
            onClick={() => supabase.auth.signOut()}
            className="mt-2 w-full rounded border border-border px-2 py-1 text-left hover:bg-bg"
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="overflow-y-auto p-4 md:p-6">{children}</main>
      <CommandPalette />
    </div>
  );
}
